import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  InputLabel,
} from "@mui/material";
import { useEffect, useState, useCallback, useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import apiRoutes from "../../store/apiConfigs";
import DataGridComponent from "../../components/DataGridComponent";
import { assignedObjectListApi } from "../../store/channel/assignedObjectsListing";
import { assignObjectToChannelApi } from "../../store/channel/assignObjectToChannel";
import { unAssignObjectFromChannelApi } from "../../store/channel/unAssignObjectFromChannel";
import CustomGridToolbar from "../../components/CustomGridToolbarWithoutDownload";
import CheckIcon from "@mui/icons-material/Check";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import RemoveIcon from "@mui/icons-material/Remove";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { AuthContext } from "../../context/AuthContext";
import { uploadImageApi } from "../../store/upload/uploadImage";
import { categoryApi } from "../../store/categoryDetails/categoryList";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}
CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

// PublishModal Component
function PublishModal({ open, onClose, onPublish, record, loading, categories }) {
  const dispatch = useDispatch();

  // Create a title → id lookup map
  const categoryTitleMap = useMemo(() => {
    return (categories || []).reduce((acc, cat) => { // CHANGED: build lookup map instead of using Array.find
      acc[cat.title] = cat.id; // CHANGED: store title-id mapping
      return acc; 
    }, {}); 
  }, [categories]);

  // Derive the initial category ID from record
  const derivedCategoryId = useMemo(() => {
    const categoryTitle =
      record?.custom_category || record?.object_data?.category; // CHANGED: normalize category source

    if (!categoryTitle) return ''; 

    return categoryTitleMap[categoryTitle] || ''; // CHANGED: O(1) lookup instead of Array.find
  }, [record?.custom_category, record?.object_data?.category, categoryTitleMap]);

  // Keep original object's category id (based on record.object_data.category)
  const originalObjectCategoryId = useMemo(() => {
    const title = record?.object_data?.category;
    if (!title) return '';
    return categoryTitleMap[title] || '';
  }, [record?.object_data?.category, categoryTitleMap]);


  const [thumbnailOption, setThumbnailOption] = useState("default");
  const [customThumbnail, setCustomThumbnail] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);

  // Keep track of which record we're showing
  const [currentRecordId, setCurrentRecordId] = useState(record?.id);
  const [selectedCategory, setSelectedCategory] = useState(derivedCategoryId);

  // Initialize/reset state when modal opens or record changes
  useEffect(() => {
    if (!open) return;
    const recId = record?.id;
    setCurrentRecordId(recId);
    setSelectedCategory(derivedCategoryId);

    // If the record already has a custom thumbnail, show it; otherwise default
    const existingCustom = record?.custom_thumbnail || record?.customThumbnail || null;
    setCustomThumbnail(existingCustom);
    setThumbnailOption(existingCustom ? "custom" : "default");
  }, [open, record?.id, record?.custom_thumbnail, record?.customThumbnail, derivedCategoryId]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadLoading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const action = await dispatch(uploadImageApi(formData));
      if (!action.type.includes("rejected") && action.payload?.result) {
        const imageUrl = action.payload.result.image_url || action.payload.result.url || action.payload.result;
        setCustomThumbnail(imageUrl);
      }
    } catch (err) {
      console.log("Upload error:", err);
    } finally {
      setUploadLoading(false);
    }
  };

  const handlePublish = useCallback(() => {
    const publishData = {};

    // If user selected default, explicitly send null and clear any uploaded image
    if (thumbnailOption === "default") {
      publishData.thumbnail = null;
      setCustomThumbnail(null);
    } else {
      // custom selected: require upload if none present
      if (!customThumbnail) {
        console.log("Please upload a thumbnail image");
        return;
      }
      publishData.thumbnail = customThumbnail;
    }

    // If user selected the original object's category, send null for custom_category
    if (selectedCategory && selectedCategory !== originalObjectCategoryId) {
      publishData.category = selectedCategory;
    } else {
      publishData.category = null;
    }

    onPublish(publishData);
  }, [thumbnailOption, customThumbnail, selectedCategory, onPublish]);

  // When switching to default option clear any existing custom thumbnail immediately
  useEffect(() => {
    if (thumbnailOption === "default" && customThumbnail) {
      setCustomThumbnail(null);
    }
  }, [thumbnailOption]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2 }}>
        <DialogTitle sx={{ p: 0 }}>Publish {record?.object_data?.title || "Module"}</DialogTitle>
        <Button
          onClick={handleClose}
          sx={{ minWidth: "auto", p: 0.5 }}
          disabled={loading || uploadLoading}
        >
          ✕
        </Button>
      </Box>
      <DialogContent sx={{ pt: 2 }}>
        {/* Category Row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Typography sx={{ fontWeight: 600, minWidth: "120px" }}>Category :</Typography>
          <FormControl variant="outlined" fullWidth>
            <InputLabel shrink>Category</InputLabel>
            <Select
              label="Category"
              id="outlined-adornment-category"
              notched
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categories?.slice().sort((a, b) => a.title.localeCompare(b.title))?.map(({ id, title }) => (
                <MenuItem value={id} key={id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ mr: 1 }}>{title}</Box>
                    {id === originalObjectCategoryId && (
                      <Box sx={{ ml: 'auto', bgcolor: 'transparent' }}>
                        <Box sx={{ bgcolor: 'primary.main', color: '#fff', borderRadius: '12px', px: 1, py: '2px', fontSize: '0.7rem' }}>
                          Default
                        </Box>
                      </Box>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Thumbnail Row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Typography sx={{ fontWeight: 600, minWidth: "120px" }}>Thumbnail :</Typography>
          <RadioGroup
            row
            value={thumbnailOption}
            onChange={(e) => setThumbnailOption(e.target.value)}
          >
            <FormControlLabel value="default" control={<Radio />} label="default" />
            <FormControlLabel value="custom" control={<Radio />} label="custom" />
          </RadioGroup>
        </Box>

        <Box sx={{ mb: 3, width: "100%", height: "250px", overflow: "hidden", borderRadius: "4px", backgroundColor: "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {thumbnailOption === "default" ? (
            <img
              src={record?.object_data?.banner || 'Error in preview'}
              alt="Default Thumbnail"
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "cover" }}
            />
          ) : customThumbnail ? (
            <img
              src={customThumbnail}
              alt="Custom Thumbnail"
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "cover" }}
            />
          ) : null}
        </Box>

        {thumbnailOption === "custom" && (
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
            disabled={uploadLoading}
            fullWidth
          >
            {uploadLoading ? "Uploading..." : "Choose Image"}
            <input hidden accept="image/*" type="file" onChange={handleFileUpload} />
          </Button>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", pb: 2, pt: 1 }}>
        <Button onClick={handleClose} variant="outlined" disabled={loading || uploadLoading}>
          Back
        </Button>
        <Button onClick={handlePublish} variant="contained" color="success" disabled={loading || uploadLoading}>
          {loading ? <CircularProgress size={24} /> : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

PublishModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onPublish: PropTypes.func.isRequired,
  record: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  categories: PropTypes.array.isRequired,
};

export default function ChannelAssignedModuleList({ getRowSpacing, NoRowsOverlay, channelCategory, channel_id, callModulelistList }) {
  const { result: assignedObjectListResult, loading: assignedObjectLoading, total: assignedObjectTotal } = useSelector((state) => state?.assignedObjectList);
  const categoriesState = useSelector((state) => state?.categories);
  const categoriesResult = categoriesState?.result || [];

  const dispatch = useDispatch();
  const auth = useContext(AuthContext);
  const [queryOptionsChangeAssignedModule, setQueryOptionsChangeAssignedModule] = useState({});
  const [paginationModelChangeAssignedModule, setPaginationModelChangeAssignedModule] = useState({
    page: 0,
    pageSize: 20,
  });

  // Modal state
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [publishLoading, setPublishLoading] = useState(false);

  const paginationModelAssignedMemoized = useMemo(() => paginationModelChangeAssignedModule, [paginationModelChangeAssignedModule]);

  const onFilterChangeAssignedModule = useCallback((filterModel) => {
    setQueryOptionsChangeAssignedModule({ filterModel: { ...filterModel } });
  }, []);

  const createQueryParams = useCallback((paginationModel, queryOptions) => {
    const filterModel = queryOptions?.filterModel;
    const items = filterModel?.items;
    const firstItem = items && items.length > 0 ? items[0] : {};
    const quickFilterValues = filterModel?.quickFilterValues;

    return {
      page: paginationModel?.page + 1,
      page_size: paginationModel?.pageSize,
      field: firstItem.field || '',
      operator: firstItem.operator || (quickFilterValues && "contains") || '',
      search: firstItem.value || (quickFilterValues && quickFilterValues.toString().replaceAll(",", " ")) || '',
      value: firstItem.value || (quickFilterValues && quickFilterValues.toString().replaceAll(",", " ")) || '',
      channel_id,
      type: channelCategory
    };
  }, [channel_id, channelCategory]);

  const callObjectList = useCallback(() => {
    const queryAssignedModule = createQueryParams(paginationModelChangeAssignedModule, queryOptionsChangeAssignedModule);
    dispatch(assignedObjectListApi(queryAssignedModule));
  }, [createQueryParams, dispatch, paginationModelChangeAssignedModule, queryOptionsChangeAssignedModule])

  // Fetch categories on component mount
  useEffect(() => {
    dispatch(categoryApi());
  }, [dispatch]);

  const handleRemoveFromChannel = async (record) => {
    dispatch(unAssignObjectFromChannelApi({
      type: record.module_type ? record.module_type === "Course" ? "Courses" : record.module_type : record.module_type,
      ID: record.id
    })).then((action) => {
      if (!action.type.includes("rejected")) {
        callObjectList()
        callModulelistList()
      }
    }).catch(err => console.log(err))
  }

  const handlePublishClick = (record) => {
    if (!record.published) {
      // Open modal only when published is false
      setSelectedRecord(record);
      setPublishModalOpen(true);
    } else {
      // If already published, unpublish directly
      handlePublishUnpublishChannel(record);
    }
  }

  const handlePublishUnpublishChannel = async (record) => {
    const publishData = typeof record === 'object' && record.row ? record.row : record;

    dispatch(assignObjectToChannelApi({
      type: publishData.module_type ? publishData.module_type === "Course" ? "Courses" : publishData.module_type : publishData.module_type,
      module_id: publishData.module_id,
      channel_id,
      published: !publishData.published,
      category: publishData.category || null,
      custom_thumbnail: publishData.customThumbnail || null,
    })).then((action) => {
      if (!action.type.includes("rejected")) {
        callObjectList()
      }
    }).catch(err => console.log(err))
  }

  const handlePublishFromModal = (publishData) => {
    setPublishLoading(true);

    dispatch(assignObjectToChannelApi({
      type: selectedRecord.module_type ? selectedRecord.module_type === "Course" ? "Courses" : selectedRecord.module_type : selectedRecord.module_type,
      module_id: selectedRecord.module_id,
      channel_id,
      published: true,
      custom_thumbnail: publishData.thumbnail,
      custom_category: publishData.category,
    })).then((action) => {
      if (!action.type.includes("rejected")) {
        setPublishModalOpen(false);
        setSelectedRecord(null);
        callObjectList()
      }
    }).catch(err => {
      console.log(err);
    }).finally(() => {
      setPublishLoading(false);
    })
  }

  const handlePaginationModelChangeAssigned = (model) => {
    if (
      paginationModelChangeAssignedModule.page !== model.page ||
      paginationModelChangeAssignedModule.pageSize !== model.pageSize
    ) {
      setPaginationModelChangeAssignedModule(model);
    }
  };

  // Fetch assigned module data
  useEffect(() => {
    callObjectList()
  }, [dispatch, paginationModelAssignedMemoized, queryOptionsChangeAssignedModule, callObjectList, channelCategory]);

  const assignedObjectListColumn = [
    {
      field: "title",
      headerName: "Title",
      width: 450,
      filterable: true,
      sortable: false,
      hideable: false,
      renderCell: (data) => (<>
        {data.row.object_data.title}
      </>)
    },
    {
      field: "module_type",
      headerName: "Content Type",
      width: 200,
      filterable: false,
      sortable: false,
      hideable: false
    },
    {
      field: "actions",
      headerName: "Actions",
      filterable: false,
      sortable: false,
      width: 300,
      disableExport: true,
      renderCell: (data) => (
        <Box display={"flex"} justifyItems={"center"} alignItems={"center"} justifyContent={"space-around"} width={"100%"}>
          <Button color="success" variant="contained"
            endIcon={data?.row?.published ? <CheckIcon /> : <ArrowRightIcon />}
            onClick={() => handlePublishClick(data?.row)}
          >
            {data?.row?.published ? "UnPublish" : "Publish"}
          </Button>
          {!auth?.userData?.is_subadmin && (<Button
            color="info"
            endIcon={<RemoveIcon />}
            label="Available"
            variant="outlined"
            onClick={() => handleRemoveFromChannel(data.row)}
          >
            Remove
          </Button>)}
        </Box>
      ),
    },
  ];
  return (
    <>
      <DataGridComponent
        loading={assignedObjectLoading}
        rowCount={assignedObjectTotal}
        pageSize={paginationModelChangeAssignedModule?.pageSize}
        onPageSizeChange={(newPageSize) => {
          setPaginationModelChangeAssignedModule({
            ...paginationModelChangeAssignedModule,
            pageSize: newPageSize,
          })
        }}
        paginationModel={paginationModelAssignedMemoized}
        onPaginationModelChange={handlePaginationModelChangeAssigned}
        getRowId={(row) => `${row.id}-${row.module_id}-${row.module_type}-${uuidv4()}`}
        rows={assignedObjectListResult}
        getRowSpacing={getRowSpacing}
        columns={assignedObjectListColumn}
        onFilterModelChange={onFilterChangeAssignedModule}
        slots={{
          noRowsOverlay: NoRowsOverlay,
          toolbar: (props) =>
          (CustomGridToolbar({
            ...props, moduleType: "assignedObject", queryOptions: queryOptionsChangeAssignedModule, paginationModel: paginationModelChangeAssignedModule, apiUrl: apiRoutes.channelAction('assign-object/list')
          })),
        }}
        slotProps={{
          loadingOverlay: {
            variant: 'linear-progress',
            noRowsVariant: 'linear-progress',
          },
          toolbar: {
            showQuickFilter: true,
          },
        }}
      />
      <PublishModal
        open={publishModalOpen}
        onClose={() => {
          setPublishModalOpen(false);
          setSelectedRecord(null);
        }}
        onPublish={handlePublishFromModal}
        record={selectedRecord}
        loading={publishLoading}
        categories={categoriesResult || []}
      />
    </>
  );
}
